/* eslint no-underscore-dangle: ["error", { "allow": ["_locator", "_from", "_to"] }] */

import Vue from 'vue';
import buildApiQuery from '@/lib/buildApiQuery';
import kmToMiles from '@/lib/kmToMiles';
import isEqual from 'lodash/isEqual';
import nonreactive from '@/lib/nonreactive';
import queryString from 'query-string';
import handleErrors from '@/lib/handleErrors';

const moduleState = {
  stations: [],
  geojson: {
    type: 'FeatureCollection',
    features: [],
  },
  lineGeojson: {
    type: 'FeatureCollection',
    features: [],
  },
  searchLocationsGeojson: {
    type: 'FeatureCollection',
    features: [],
  },
  routeDirections: [],
  count: 0,
  info: null,
  csv: null,
  currentApiQuery: {},
  routeError: null,
};

const moduleGetters = {
  routeStations: (state) => state.stations,
  routeStationsGeojson: (state) => state.geojson,
  routeStationsLineGeojson: (state) => state.lineGeojson,
  routeStationsSearchLocationsGeojson: (state) => state.searchLocationsGeojson,
  routeStationsCount: (state) => state.count,
  routeStationsInfo: (state) => state.info,
  routeStationsCsv: (state) => state.csv,
  getRouteError: (state) => state.routeError,
  getRouteDirections: (state) => state.routeDirections,
};

const mutations = {
  saveRouteCurrentApiQuery(state, apiQuery) {
    state.currentApiQuery = apiQuery;
  },

  saveRouteStations(state, payload) {
    const geojson = {
      type: 'FeatureCollection',
      features: [],
    };

    const lineGeojson = {
      type: 'FeatureCollection',
      features: [],
    };

    const searchLocationsGeojson = {
      type: 'FeatureCollection',
      features: [],
    };

    if (payload && payload.data && payload.data.routes && payload.data.routes[0] && payload.data.routes[0].fuel_stations) {
      // Currently the directions page just uses the first route. If we add support for switching
      // between alternate routes, this will need to be revisited.
      const routeIndex = 0;
      const route = payload.data.routes[routeIndex];
      state.stations = nonreactive.frozen(route.fuel_stations);
      state.info = nonreactive.frozen(route._locator);
      state.count = route.total_results;
      state.routeDirections = nonreactive.frozen(route._locator.directions.maneuvers);

      const csvQuery = queryString.stringify({
        ...payload.apiQuery,
        route_index: routeIndex,
        // Include UTF8 BOM in the CSV response for better special character
        // handling with Excel (particularly for French data). This assumes
        // most people using the locator will be opening the CSVs in Excel
        // where this is needed to display UTF8 characters properly.
        utf8_bom: 'true',
      });
      state.csv = `${process.env.VUE_APP_API_URL_ROOT}/v1/locator/directions.csv?${csvQuery}`;

      searchLocationsGeojson.features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [route._locator.from.longitude, route._locator.from.latitude],
        },
        properties: {
          color: 'green',
        },
      });
      searchLocationsGeojson.features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [route._locator.to.longitude, route._locator.to.latitude],
        },
        properties: {
          color: 'red',
        },
      });

      for (let i = 0, len = route.fuel_stations.length; i < len; i += 1) {
        const station = route.fuel_stations[i];
        geojson.features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [station.longitude, station.latitude],
          },
          properties: {
            id: station.id,
            fuel: station.fuel_type_code,
          },
        });
      }

      lineGeojson.features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: route._locator.route.coordinates,
        },
      });
    } else {
      state.stations = [];
      state.count = 0;
      state.info = null;
      state.csv = null;
    }

    state.lineGeojson = nonreactive.frozen(lineGeojson);
    state.searchLocationsGeojson = nonreactive.frozen(searchLocationsGeojson);
    state.geojson = nonreactive.frozen(geojson);

    if (payload) {
      state.currentApiQuery = payload.apiQuery;
    }
  },

  setRouteError(state, status) {
    state.routeError = status;
  },
};

const actions = {
  async fetchRouteStations({ commit, state, getters }, query) {
    try {
      const apiQuery = {
        ...buildApiQuery(getters, query),
        _from: query.start,
        _to: query.end,
        distance: query.distance,
      };

      if (getters.getFilterValueWithDefault('distance_unit') === 'km') {
        apiQuery.distance = kmToMiles(apiQuery.distance);
      }

      if (isEqual(apiQuery, state.currentApiQuery)) {
        return;
      }

      commit('setLoadingRouteStations', true);
      commit('saveRouteCurrentApiQuery', apiQuery);
      // If the start or end location is empty, then return an empty result without
      // actually performing an ajax query.
      if (apiQuery._from === undefined || apiQuery._from === null || apiQuery._from === '' || apiQuery._to === undefined || apiQuery._to === null || apiQuery._to === '') {
        commit('saveRouteStations', {
          apiQuery,
        });
        commit('setLoadingRouteStations', false);
        return;
      }

      const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL_ROOT}/v1/locator/directions.json`, {
        params: apiQuery,
      });

      commit('saveRouteStations', {
        apiQuery,
        data: response.data,
      });

      commit('setRouteError', null);
    } catch (e) {
      commit('saveRouteStations');
      commit('setRouteError', handleErrors(e));
    } finally {
      commit('setLoadingRouteStations', false);
    }
  },
};

export default {
  state: moduleState,
  getters: moduleGetters,
  mutations,
  actions,
};
